/**
 * Helpers for OrganizationJobCategoriesTab (ER-11355).
 *
 * Builds list-query params, SearchBar chips (Draft/Active/Retired plus non-default
 * Archived/Used), wizard/detail API payloads, and wizard step navigation.
 */
import {
  FACTORS,
  STEPS,
  factorIsComplete,
  normalizeScores,
} from "@components/AddJobCategoryWizard/addJobCategoryWizard.js";
import { fromApiCategory } from "@components/JobCategoriesList/jobCategoriesListRow.js";
import { resolvedCategoryScores } from "@components/JobCategoryDetailPage/jobCategoryDetailPage.js";

export const PAGE_SIZE = 25;

export const DEFAULT_STATUSES = { draft: true, active: true, retired: false };

export const DEFAULT_INCLUDE_ARCHIVED = false;

export const DEFAULT_USED = "no_filter";

const CHIP_LABEL_BY_VALUE = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
  show_archived: "Show Archived",
  used: "Used",
  not_used: "Not Used",
};

const STATUS_CHIP_ORDER = ["draft", "active", "retired"];

const CHIP_VALUE_BY_LABEL = Object.keys(CHIP_LABEL_BY_VALUE).reduce((acc, value) => {
  acc[CHIP_LABEL_BY_VALUE[value]] = value;
  return acc;
}, {});

export function selectedStatuses(statuses, quickStatus) {
  if (quickStatus) return [quickStatus];
  const source = statuses && typeof statuses === "object" ? statuses : DEFAULT_STATUSES;
  return Object.keys(source).filter((key) => source[key]);
}

/**
 * Query params for GET /job-category/. `archived` is omitted when includeArchived
 * is true (show both). `used` is omitted for no_filter.
 */
export function buildListParams(filters, pagination) {
  const source = filters || {};
  const params = {
    status: selectedStatuses(source.statuses, source.quickStatus),
    search: typeof source.search === "string" ? source.search : "",
  };
  if (!source.includeArchived) {
    params.archived = false;
  }
  if (source.used === "used") {
    params.used = true;
  } else if (source.used === "not_used") {
    params.used = false;
  }
  return {
    filters: params,
    pagination: {
      page: Number((pagination && pagination.page) || 1) || 1,
      pageSize: Number((pagination && pagination.pageSize) || PAGE_SIZE) || PAGE_SIZE,
    },
  };
}

/**
 * SearchBar chips. Status chips always follow the checked statuses (defaults
 * Draft/Active still show, matching Departments / Roles). Radio chips are only
 * produced for non-default values.
 */
export function chipsFromFilters(filters) {
  const source = filters || {};
  const chips = [];
  const statuses = source.statuses && typeof source.statuses === "object" ? source.statuses : {};
  STATUS_CHIP_ORDER.forEach((key) => {
    if (statuses[key] && CHIP_LABEL_BY_VALUE[key]) {
      chips.push({ value: key, label: CHIP_LABEL_BY_VALUE[key] });
    }
  });
  if (source.includeArchived) {
    chips.push({ value: "show_archived", label: CHIP_LABEL_BY_VALUE.show_archived });
  }
  if (source.used === "used" || source.used === "not_used") {
    chips.push({ value: source.used, label: CHIP_LABEL_BY_VALUE[source.used] });
  }
  return chips;
}

/** Flat list of applied filter values (statuses, show_archived, used/not_used) for default-aware checks. */
export function appliedFilterValues(filters) {
  return chipsFromFilters(filters).map((chip) => chip.value);
}

export function chipLabels(filters) {
  return chipsFromFilters(filters).map((chip) => chip.label);
}

export function removeChipFromFilters(filters, chipLabel) {
  const next = {
    statuses: { ...DEFAULT_STATUSES, ...((filters && filters.statuses) || {}) },
    includeArchived: Boolean(filters && filters.includeArchived),
    used: (filters && filters.used) || DEFAULT_USED,
    search: (filters && filters.search) || "",
    quickStatus: (filters && filters.quickStatus) || "",
  };
  const value = CHIP_VALUE_BY_LABEL[chipLabel] || chipLabel;
  if (value === "draft" || value === "active" || value === "retired") {
    next.statuses = { ...next.statuses, [value]: false };
  } else if (value === "show_archived") {
    next.includeArchived = false;
  } else if (value === "used" || value === "not_used") {
    next.used = DEFAULT_USED;
  }
  return next;
}

/**
 * The nested `scores` map for a request body, or `undefined` when it is not
 * complete: the backend scores a category as a whole (all 14 subfactor levels)
 * and answers a partial set with JOB_CATEGORY_SUBFACTORS_INCOMPLETE.
 */
function completeScoresOf(scores) {
  const normalized = normalizeScores(scores);
  return FACTORS.every((factor) => factorIsComplete(normalized, factor)) ? normalized : undefined;
}

function bodyWithScores(payload) {
  const body = {
    name: payload && typeof payload.name === "string" ? payload.name.trim() : "",
  };
  const scores = completeScoresOf(payload && payload.scores);
  if (scores) body.scores = scores;
  return body;
}

/** POST body: `name` plus the nested subfactor `scores`; never the four factor scores (ER-11935). */
export function wizardPayloadToCreateBody(payload) {
  return bodyWithScores(payload);
}

/** PATCH body: `name` plus `scores` only when every level is set; never the four factor scores. */
export function savePayloadToUpdateBody(payload) {
  return bodyWithScores(payload);
}

const SCORES_ERROR_MESSAGES = [
  [
    "JOB_CATEGORY_SUBFACTOR_UNKNOWN",
    "One or more subfactors are not recognised. Review the scores and try again.",
  ],
  [
    "JOB_CATEGORY_SUBFACTOR_LEVEL_INVALID",
    "One or more subfactor levels are outside the allowed range. Review the scores and try again.",
  ],
  [
    "JOB_CATEGORY_SUBFACTORS_INCOMPLETE",
    "Every subfactor needs a level before the scores can be saved. Review the scores and try again.",
  ],
];

/**
 * Inline message for a backend 400 on `scores` (`{ scores: ["<CODE>: keys"] }`),
 * or "" when the error is not one of the three subfactor codes.
 */
export function jobCategoryScoresErrorMessage(error) {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object") return "";
  const raw = [data.scores, data.subfactor_scores, data.detail].flat();
  const text = raw.filter((entry) => typeof entry === "string").join(" ");
  const match = SCORES_ERROR_MESSAGES.find(([code]) => text.includes(code));
  return match ? match[1] : "";
}

export function formatUpdatedLabel(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function mapApiToDetailCategory(apiCategory) {
  const listRow = fromApiCategory(apiCategory);
  const mapped = {
    ...listRow,
    updatedLabel: formatUpdatedLabel(listRow.updatedOn),
    scores: (apiCategory && apiCategory.scores) || {},
  };
  return {
    ...mapped,
    scores: resolvedCategoryScores(mapped),
  };
}

export function pageCountFromResponse(data, pageSize = PAGE_SIZE) {
  const count = Number(data && data.count);
  const size = Number(pageSize) || PAGE_SIZE;
  if (!Number.isFinite(count) || count <= 0) return 1;
  return Math.max(1, Math.ceil(count / size));
}

/** Backend destroy() returns 400 `{ detail: "JOB_CATEGORY_HAS_POSITIONS" }` when assigned. */
export function isJobCategoryInUseError(error) {
  const detail = error && error.response && error.response.data && error.response.data.detail;
  return detail === "JOB_CATEGORY_HAS_POSITIONS";
}

export function nextWizardStep(step) {
  const index = STEPS.findIndex((entry) => entry.key === step);
  if (index === -1 || index >= STEPS.length - 1) return step;
  return STEPS[index + 1].key;
}

export function prevWizardStep(step) {
  const index = STEPS.findIndex((entry) => entry.key === step);
  if (index <= 0) return step;
  return STEPS[index - 1].key;
}
